import { FormEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import arkieFrontImage from '../../assets/images/arkiebyte/arkiebyte-front.png';
import arkieFrontSparklesImage from '../../assets/images/arkiebyte/arkiebyte-front-sparkles.png';
import arkieSideImage from '../../assets/images/arkiebyte/arkiebyte-side.png';
import arkieSurprisedImage from '../../assets/images/arkiebyte/arkiebyte-surprised.png';
import arkieThinkingImage from '../../assets/images/arkiebyte/arkiebyte-thinking.png';
import {
  getHogcloudChatReply,
  hogcloudChatIntro,
  hogcloudChatQuickPrompts,
  HogcloudChatPose,
} from '../Home/chatbotContent';
import '../Home/index.css';
import './index.css';

const chatbotPoseImages: Record<HogcloudChatPose, string> = {
  neutral: arkieFrontImage,
  focused: arkieSideImage,
  thinking: arkieThinkingImage,
  helpful: arkieSideImage,
  surprised: arkieSurprisedImage,
  celebrate: arkieFrontSparklesImage,
};

type ChatMessage = {
  id: number;
  sender: 'bot' | 'visitor';
  content: ReactNode;
  htmlContent?: string;
};

const faqs = [
  {
    question: 'What services does Hogcloud offer?',
    answer: 'Hogcloud provides websites, app development, cloud and technology consulting, AI automation and chatbots, professional email setup, and custom 3D printing.',
  },
  {
    question: 'Does Hogcloud only work with Northwest Arkansas customers?',
    answer: 'No. Hogcloud is based in Fayetteville and works with customers throughout Northwest Arkansas, but many website, app, cloud, AI, and consulting projects can be completed remotely.',
  },
  {
    question: 'How much does a simple website cost?',
    answer: 'A very simple five-page website can be set up for under $500 when the customer provides ready-to-use content and the project has limited custom integrations.',
  },
  {
    question: 'Can Hogcloud build a website with customer or member logins?',
    answer: 'Yes. Membership and login websites start at $900 and can grow to include customer portals, ordering, scheduling, dashboards, payments, and other cloud-connected features.',
  },
  {
    question: 'Can Hogcloud help keep website hosting affordable?',
    answer: 'Yes. Hogcloud uses compact site delivery, CDN caching, and package-size optimization to reduce repeated data reads and keep monthly hosting costs as low as practical.',
  },
  {
    question: 'What should I provide for a website estimate?',
    answer: 'Send your business or project goal, desired launch date, budget range, example websites, required features, existing domain or hosting details, and any available text, logos, or photos.',
  },
  {
    question: 'Can Hogcloud build mobile apps and custom business tools?',
    answer: 'Yes. Projects can include customer logins, messaging, scheduling, ordering, intake forms, dashboards, service requests, payments, cloud databases, and third-party integrations.',
  },
  {
    question: 'How does Hogcloud approach a new app idea?',
    answer: 'Hogcloud starts by identifying the users, main workflow, required data, and smallest useful first release. Additional features can be added after the initial version is tested with real users.',
  },
  {
    question: 'What is a RAG-powered chatbot?',
    answer: 'It is an AI assistant grounded in business documents such as FAQs, services, prices, policies, hours, and product details. It retrieves relevant source information before answering common customer questions.',
  },
  {
    question: 'Can Hogcloud set up a professional business email domain?',
    answer: 'Yes. Hogcloud can assist with the domain, DNS authentication, mailbox setup, and documentation. Domain registration is typically $10–$15 per year, mailboxes can be about $1 per month each, and the documented one-time setup fee is $200.',
  },
  {
    question: 'What information is needed for a custom 3D print?',
    answer: 'Provide the intended use, approximate size, quantity, colors, deadline, reference photos or sketches, and any logo or artwork files. Also note whether the item needs to be decorative, durable, flexible, or precise.',
  },
  {
    question: 'Are all custom 3D prints suitable for food, heat, or heavy loads?',
    answer: 'No. Unless specifically discussed, 3D printed items should not be assumed to be food-safe, heat-resistant, load-bearing, child-safe, dishwasher-safe, or appropriate for critical mechanical use.',
  },
  {
    question: 'Does Hogcloud offer ongoing support?',
    answer: 'Yes. Retainers start at $400–$800 per month for agreed ongoing work such as website updates, cloud guidance, maintenance, chatbot document updates, and technical planning.',
  },
  {
    question: 'What experience does Hogcloud bring to a project?',
    answer: 'Owner Matt Chandler has more than 25 years of technology experience across cloud architecture, full-stack development, enterprise systems, business software, maker education, AI, and small-business consulting.',
  },
  {
    question: 'What are Hogcloud’s normal business hours?',
    answer: 'Normal hours are 8:00 AM to 4:00 PM Central Time. Messages can be submitted at any time and are generally handled during business hours.',
  },
];

const FAQ = () => {
  const chatMessagesEndRef = useRef<HTMLDivElement | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    { id: 1, sender: 'bot', content: hogcloudChatIntro.content },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatAvatarPose, setChatAvatarPose] = useState<HogcloudChatPose>(hogcloudChatIntro.pose);
  const [isChatWaiting, setIsChatWaiting] = useState(false);
  const chatbotApiUrl = process.env.REACT_APP_HOGCLOUD_CHATBOT_API_URL;

  useEffect(() => {
    if (isChatOpen) {
      chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [chatMessages, isChatOpen]);

  const appendChatMessage = (
    sender: ChatMessage['sender'],
    content: ReactNode,
    htmlContent?: string,
  ) => {
    setChatMessages((current) => [
      ...current,
      { id: Date.now() + Math.random(), sender, content, htmlContent },
    ]);
  };

  const getChatbotApiReply = async (message: string): Promise<string | null> => {
    if (!chatbotApiUrl) return null;

    const response = await fetch(chatbotApiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.message || 'Chatbot request failed.');
    return typeof payload.answer === 'string' ? payload.answer : null;
  };

  const sendChatMessage = async (message: string) => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage || isChatWaiting) return;

    if (['exit', 'bye', 'goodbye', 'close'].includes(trimmedMessage.toLowerCase())) {
      setChatInput('');
      setIsChatOpen(false);
      return;
    }

    appendChatMessage('visitor', trimmedMessage);
    setChatInput('');
    setIsChatWaiting(true);
    setChatAvatarPose('thinking');

    try {
      const apiReply = await getChatbotApiReply(trimmedMessage);
      if (apiReply) {
        appendChatMessage('bot', null, apiReply);
        setChatAvatarPose('helpful');
      } else {
        const botReply = getHogcloudChatReply(trimmedMessage);
        appendChatMessage('bot', botReply.content);
        setChatAvatarPose(botReply.pose);
      }
    } catch {
      const botReply = getHogcloudChatReply(trimmedMessage);
      appendChatMessage('bot', botReply.content);
      setChatAvatarPose(botReply.pose);
    } finally {
      setIsChatWaiting(false);
    }
  };

  const handleChatSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    sendChatMessage(chatInput);
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <main className="faq-page">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <section className="faq-hero">
        <p className="faq-kicker">Hogcloud FAQ</p>
        <h1>Frequently Asked Questions</h1>
        <p>Get answers to frequently asked questions about our services.</p>
      </section>

      <section className="faq-list" aria-label="Frequently asked questions">
        {faqs.map((faq) => (
          <details className="faq-item" key={faq.question}>
            <summary>{faq.question}</summary>
            <p>{faq.answer}</p>
          </details>
        ))}
      </section>

      <section className="faq-contact" aria-labelledby="faq-contact-title">
        <div>
          <h2 id="faq-contact-title">Have another question?</h2>
          <p>Tell Hogcloud what you want to build, your timeline, and the result you need.</p>
        </div>
        <div className="home-chatbot-start" id="arkiebyte">
            <img src={arkieFrontSparklesImage} alt="" aria-hidden="true" />
            <div>
                <p>Have a quick question?</p>
                <button
                    type="button"
                    className="home-chatbot-start__button"
                    onClick={() => setIsChatOpen(true)}
                >
                    Chat with ArkieByte
                </button>
            </div>
        </div>
        <div className="faq-contact-actions">
          <Link to="/contact">Contact Hogcloud</Link>
        </div>
      </section>

      <div className="leave-message" aria-live="polite">
        {isChatOpen && (
          <section className="leave-message__panel" aria-labelledby="faq-chat-title">
            <div className="leave-message__header">
              <div className="chatbot__header-intro">
                <img
                  className={`chatbot__avatar ${chatInput ? 'chatbot__avatar--typing' : ''}`}
                  src={chatbotPoseImages[chatAvatarPose]}
                  alt=""
                  aria-hidden="true"
                />
                <div>
                  <p className="chatbot__eyebrow" id="faq-chat-title">Hogcloud Chat</p>
                  <p>Ask me anything quick.</p>
                </div>
              </div>
              <button
                className="leave-message__close"
                type="button"
                aria-label="Close chat"
                onClick={() => setIsChatOpen(false)}
              >
                ×
              </button>
            </div>

            <div className="chatbot__messages" aria-label="Chat conversation">
              {chatMessages.map((message) => (
                message.htmlContent ? (
                  <div
                    className={`chatbot__message chatbot__message--${message.sender}`}
                    dangerouslySetInnerHTML={{ __html: message.htmlContent }}
                    key={message.id}
                  />
                ) : (
                  <div className={`chatbot__message chatbot__message--${message.sender}`} key={message.id}>
                    {message.content}
                  </div>
                )
              ))}
              {isChatWaiting && (
                <div className="chatbot__message chatbot__message--bot">Checking Hogcloud docs...</div>
              )}
              <div ref={chatMessagesEndRef} />
            </div>

            <div className="chatbot__quick-actions" aria-label="Quick chat options">
              {hogcloudChatQuickPrompts.map((prompt) => (
                <button
                  type="button"
                  key={prompt.message}
                  disabled={isChatWaiting}
                  onClick={() => sendChatMessage(prompt.message)}
                >
                  {prompt.label}
                </button>
              ))}
            </div>

            <form className="chatbot__input-form" onSubmit={handleChatSubmit}>
              <label htmlFor="faq-chatbot-input" className="chatbot__input-label">Ask a question</label>
              <div className="chatbot__input-row">
                <input
                  id="faq-chatbot-input"
                  type="text"
                  value={chatInput}
                  placeholder="Ask about websites, apps, 3D prints..."
                  disabled={isChatWaiting}
                  onFocus={() => setChatAvatarPose('focused')}
                  onChange={(event) => {
                    setChatInput(event.target.value);
                    setChatAvatarPose(event.target.value ? 'thinking' : hogcloudChatIntro.pose);
                  }}
                />
                <button className="chatbot__send-button" type="submit" disabled={isChatWaiting}>Send</button>
              </div>
            </form>
          </section>
        )}
      </div>
    </main>
  );
};

export default FAQ;
